// GalleryPage.jsx — Pancakeopedia v1
// Public gallery at /gallery and /gallery/{id}. Coverflow-style rolodex
// browser: center pancake is full-size, neighbors fall away in scale and
// opacity. Swipe/drag to browse, momentum-based. Detail panel slides up
// below the focused pancake.
//
// Data: config/publicGallery (PII-safe, public-readable). Meditation text
// loaded on demand from config/publicMeditations for the focused pancake only.

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { trackGalleryView } from "../../utils/analytics";
import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import PanBar from "./components/PanBar";

const BG = "#081a2a";
const CARD_GAP = 0.72; // fraction of card width between centers
const VISIBLE_NEIGHBORS = 3; // how many cards rendered on each side
const PRELOAD_RANGE = 15; // preload images this many cards ahead/behind

function fmtMs(ms) {
  if (ms == null || ms < 0) return null;
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function fmtDate(ms) {
  if (!ms) return null;
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const TYPE_LABELS = { flash: "Flash", custom: "Custom", freestyle_flash: "Freestyle" };

export default function GalleryPage({ initialId }) {
  const [items, setItems] = useState(null);
  const [meditations, setMeditations] = useState({});
  const [idx, setIdx] = useState(0);
  const [dragOffset, setDragOffset] = useState(0); // px offset during drag
  const [pinchScale, setPinchScale] = useState(1);
  const [pinchTx, setPinchTx] = useState(0);
  const [pinchTy, setPinchTy] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [viewMode, setViewMode] = useState("rolodex"); // "rolodex" | "grid"
  const [gridFocus, setGridFocus] = useState(null); // index of pancake selected in grid view
  const [visibleCount, setVisibleCount] = useState(40); // load-more pagination
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | fastest-draw | fastest-total
  const [filterTag, setFilterTag] = useState(null);
  const [filterType, setFilterType] = useState(null); // flash | custom | freestyle_flash
  const [showcaseOnly, setShowcaseOnly] = useState(true); // default to Dan-curated showpieces
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null); // { startDist }

  // Load gallery data
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config/publicGallery"),
      s => { if (s.exists()) setItems(s.data()?.items || []); else setItems([]); },
      () => setItems([]),
    );
    return unsub;
  }, []);

  // Load meditations index for on-demand lookup
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config/publicMeditations"),
      s => {
        if (s.exists()) {
          const map = {};
          for (const m of s.data()?.items || []) map[m.id] = m;
          setMeditations(map);
        }
      },
      () => {},
    );
    return unsub;
  }, []);

  // Unique tags across all items — for filter pills
  const allTags = useMemo(() => {
    if (!items) return [];
    const counts = {};
    items.forEach(p => (p.tags || []).forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([tag]) => tag);
  }, [items]);

  // Are any pancakes flagged as showpieces? Drives the "Showpieces" default —
  // falls back to showing everything until Dan has starred at least one, so the
  // gallery is never empty.
  const hasShowcases = useMemo(() => (items || []).some(p => p.isShowcase), [items]);

  // Filtered + sorted items — must be declared before useEffects that reference it
  const displayed = useMemo(() => {
    if (!items) return [];
    let list = items;
    if (showcaseOnly && hasShowcases) list = list.filter(p => p.isShowcase);
    if (filterTag) list = list.filter(p => (p.tags || []).includes(filterTag));
    if (filterType) list = list.filter(p => p.orderType === filterType);
    list = [...list]; // copy before sort
    switch (sortBy) {
      case "oldest": list.reverse(); break;
      case "fastest-draw": list.sort((a, b) => (a.drawTimeMs || Infinity) - (b.drawTimeMs || Infinity)); break;
      case "fastest-total": list.sort((a, b) => {
        const ta = (a.drawTimeMs || 0) + (a.cookTimeMs || 0) || Infinity;
        const tb = (b.drawTimeMs || 0) + (b.cookTimeMs || 0) || Infinity;
        return ta - tb;
      }); break;
      default: break; // newest = default order from publicGallery
    }
    return list;
  }, [items, filterTag, filterType, sortBy, showcaseOnly, hasShowcases]);

  // Reset visible count when filters change
  useEffect(() => { setVisibleCount(40); }, [filterTag, filterType, sortBy, showcaseOnly]);

  // Close lightbox on index change + track gallery view
  useEffect(() => {
    setLightbox(false);
    if (displayed && displayed[idx]) trackGalleryView(displayed[idx].id, displayed[idx].title);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link override: a /gallery/{id} link (e.g. a PancakeDrift tap) targets a
  // specific pancake. The default view is Showpieces-only, so if the linked
  // pancake isn't starred it would never appear in `displayed` and the resolver
  // below would silently fall back to showpiece #0. Drop out of the Showpieces
  // filter once, on first load, when the target isn't a showpiece — so the
  // linked pancake is actually reachable.
  const deepLinkResolvedRef = useRef(false);
  useEffect(() => {
    if (deepLinkResolvedRef.current) return;
    if (!initialId || !items || items.length === 0) return;
    const target = items.find(p => p.id === initialId);
    if (target && !target.isShowcase) setShowcaseOnly(false);
    deepLinkResolvedRef.current = true;
  }, [items, initialId]);

  // Set initial index from URL param + clamp idx when displayed changes
  useEffect(() => {
    if (!displayed || displayed.length === 0) return;
    if (initialId) {
      const i = displayed.findIndex(p => p.id === initialId);
      if (i >= 0) { setIdx(i); return; }
    }
    setIdx(prev => Math.min(prev, displayed.length - 1));
  }, [displayed, initialId]);

  // URL update disabled for now — was causing white screen reloads.
  // TODO: restore once root cause is identified.

  useEffect(() => {
    document.title = "Gallery · The Joy of Pancakes";
    // Suppress macOS swipe-back navigation while on gallery page
    const html = document.documentElement;
    const prevOverscroll = html.style.overscrollBehaviorX;
    html.style.overscrollBehaviorX = "none";
    document.body.style.overscrollBehaviorX = "none";
    return () => {
      document.title = "The Joy of Pancakes";
      html.style.overscrollBehaviorX = prevOverscroll || "";
      document.body.style.overscrollBehaviorX = "";
    };
  }, []);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") { setLightbox(false); setIdx(i => Math.max(0, i - 1)); }
      if (e.key === "ArrowRight") { setLightbox(false); setIdx(i => displayed ? Math.min(displayed.length - 1, i + 1) : i); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [displayed]);

  // Trackpad / scroll wheel — capture at document level to prevent
  // macOS swipe-back gesture. Horizontal deltaX is intercepted and
  // routed to the carousel; vertical scroll passes through.
  const wheelAccum = useRef(0);
  const wheelTimer = useRef(null);
  useEffect(() => {
    const onWheel = (e) => {
      // Only intercept clearly horizontal scroll — deltaX must dominate
      // deltaY by at least 2:1 ratio to avoid hijacking vertical scrolls
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) * 1.5 && Math.abs(e.deltaX) > 5) {
        e.preventDefault();
        wheelAccum.current += e.deltaX;
        const threshold = 60;
        if (Math.abs(wheelAccum.current) >= threshold) {
          const steps = Math.sign(wheelAccum.current);
          setIdx(i => displayed ? Math.max(0, Math.min(displayed.length - 1, i + steps)) : i);
          wheelAccum.current = 0;
        }
        clearTimeout(wheelTimer.current);
        wheelTimer.current = setTimeout(() => { wheelAccum.current = 0; }, 150);
      }
    };
    document.addEventListener("wheel", onWheel, { passive: false });
    return () => document.removeEventListener("wheel", onWheel);
  }, [displayed]);

  // Preload images ahead of current position so fast scrolling stays smooth
  const preloadedRef = useRef(new Set());
  useEffect(() => {
    if (!displayed || displayed.length === 0) return;
    const start = Math.max(0, idx - PRELOAD_RANGE);
    const end = Math.min(displayed.length - 1, idx + PRELOAD_RANGE);
    for (let i = start; i <= end; i++) {
      const url = displayed[i].thumbUrl || displayed[i].cutoutUrl;
      if (url && !preloadedRef.current.has(url)) {
        preloadedRef.current.add(url);
        const img = new Image();
        img.src = url;
      }
    }
  }, [idx, displayed]);

  // Card width — responsive
  const cardSize = useMemo(() => {
    if (typeof window === "undefined") return 280;
    const w = window.innerWidth;
    if (w >= 1024) return 320;
    if (w >= 768) return 280;
    return Math.min(260, w * 0.65);
  }, []);

  // Touch/mouse drag — continuous: dragOffset drives a fractional index shift,
  // so dragging through multiple card widths advances multiple pancakes.
  function getDist(t) {
    return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  }
  function getMid(t) {
    return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 };
  }

  function onDragStart(e) {
    if (e.touches && e.touches.length >= 2) {
      const mid = getMid(e.touches);
      pinchRef.current = {
        startDist: getDist(e.touches), startScale: pinchScale,
        startMidX: mid.x, startMidY: mid.y,
        startTx: pinchTx, startTy: pinchTy,
      };
      dragRef.current = null;
      return;
    }
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startX: x, startY: y, lastX: x, startIdx: idx, startTime: Date.now(), moved: false, locked: null };
  }
  function onDragMove(e) {
    if (e.touches && e.touches.length >= 2 && pinchRef.current) {
      const dist = getDist(e.touches);
      const mid = getMid(e.touches);
      const newScale = Math.max(1, Math.min(4, pinchRef.current.startScale * (dist / pinchRef.current.startDist)));
      setPinchScale(newScale);
      setPinchTx(pinchRef.current.startTx + (mid.x - pinchRef.current.startMidX));
      setPinchTy(pinchRef.current.startTy + (mid.y - pinchRef.current.startMidY));
      return;
    }
    if (!dragRef.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = x - dragRef.current.startX;
    const dy = y - dragRef.current.startY;

    // Lock direction on first significant movement
    if (!dragRef.current.locked && (Math.abs(dx) > 64 || Math.abs(dy) > 64)) {
      dragRef.current.locked = Math.abs(dx) >= Math.abs(dy) ? "h" : "v";
    }

    // Vertical lock — let the browser scroll, don't interfere
    if (dragRef.current.locked === "v") return;

    // Horizontal lock — prevent vertical scroll, handle carousel
    if (dragRef.current.locked === "h" && e.cancelable) {
      e.preventDefault();
    }

    if (Math.abs(dx) > 5) dragRef.current.moved = true;
    dragRef.current.lastX = x;
    setDragOffset(dx);
  }
  function onDragEnd(e) {
    // Pinch release — snap back
    if (pinchRef.current) {
      pinchRef.current = null;
      setPinchScale(1);
      setPinchTx(0);
      setPinchTy(0);
      return;
    }
    const drag = dragRef.current;
    if (!drag || !displayed) { dragRef.current = null; setDragOffset(0); return; }
    dragRef.current = null;

    let x;
    try { x = e.changedTouches ? e.changedTouches[0].clientX : e.clientX; }
    catch { x = drag.lastX || drag.startX; }

    const totalDx = x - drag.startX;
    const dt = Math.max(Date.now() - drag.startTime, 1);
    const velocity = totalDx / dt;
    const cardStep = cardSize * CARD_GAP || 200;

    let rawAdvance = -totalDx / cardStep;
    const absVel = Math.abs(velocity);

    // Fast flick — coast through multiple cards with deceleration
    if (absVel > 0.8) {
      const dir = velocity > 0 ? -1 : 1;
      const extraCards = Math.min(8, Math.round(absVel * 3));
      const base = Math.round(rawAdvance);
      const finalTarget = Math.max(0, Math.min(displayed.length - 1, drag.startIdx + base + dir * extraCards));
      setDragOffset(0);

      // Animate through intermediate cards
      let step = 0;
      const total = Math.abs(finalTarget - (drag.startIdx + base));
      if (total > 0) {
        const startIdx = drag.startIdx + base;
        const clamped = Math.max(0, Math.min(displayed.length - 1, startIdx));
        setIdx(clamped);
        const tick = () => {
          step++;
          const progress = step / total;
          // Ease-out deceleration
          const current = Math.round(clamped + (finalTarget - clamped) * (1 - Math.pow(1 - progress, 2)));
          setIdx(Math.max(0, Math.min(displayed.length - 1, current)));
          if (step < total) {
            // Accelerating delay — starts fast, slows down
            setTimeout(tick, 40 + progress * 120);
          }
        };
        setTimeout(tick, 30);
      } else {
        setIdx(finalTarget);
      }
      return;
    }

    if (absVel > 0.5) rawAdvance += -velocity * 0.8;

    const advance = Math.round(rawAdvance);
    const len = displayed.length;
    const target = Math.max(0, Math.min(len - 1, drag.startIdx + advance));
    setIdx(target);
    setDragOffset(0);
  }

  if (items === null) {
    return (
      <div style={{ background: BG, minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)" }}>
        <SiteNav />
        <div style={{ padding: "8rem 1rem", textAlign: "center", color: "rgba(255,218,173,0.5)" }}>Loading gallery...</div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{ background: BG, minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)" }}>
        <SiteNav />
        <div style={{ padding: "8rem 1rem", textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🥞</div>
          <div style={{ color: "rgba(255,218,173,0.6)" }}>No pancakes in the gallery yet.</div>
        </div>
      </div>
    );
  }

  const safeIdx = Math.max(0, Math.min(idx, displayed.length - 1));
  const focused = displayed[safeIdx];
  const meditation = focused ? meditations[focused.id] : null;
  if (!focused) return (
    <div style={{ background: BG, minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)" }}>
      <SiteNav />
      <div style={{ padding: "8rem 1rem", textAlign: "center", color: "rgba(255,218,173,0.5)" }}>Loading...</div>
    </div>
  );

  return (
    <div style={{ background: BG, minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)", overscrollBehaviorX: "none" }}>
      <style>{`
        @keyframes jopPopIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
        @keyframes jopPopFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <SiteNav />

      <main style={{ padding: "5rem 0 2rem", overflow: "hidden" }}>
        {/* Header */}
        <header style={{ textAlign: "center", marginBottom: "1.5rem", padding: "0 1.25rem" }}>
          <PanBar width={72} height={3} margin="0 auto 1rem" />
          <h1 style={{
            margin: 0,
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(1.8rem, 4.5vw, 2.8rem)",
            color: "var(--jop-gold)",
            fontWeight: 700,
          }}>
            The Gallery
          </h1>
          <p style={{ margin: "0.5rem auto 0", color: "rgba(255,218,173,0.5)", fontSize: "0.85rem" }}>
            {displayed.length}{displayed.length !== items.length ? ` of ${items.length}` : ""} pancakes{viewMode === "rolodex" ? ` · ${safeIdx + 1} of ${displayed.length}` : ""}
          </p>
          <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "center", gap: "0.4rem" }}>
            <button onClick={() => setViewMode("rolodex")} style={{
              background: viewMode === "rolodex" ? "rgba(252,179,31,0.15)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${viewMode === "rolodex" ? "rgba(252,179,31,0.4)" : "rgba(255,255,255,0.12)"}`,
              borderRadius: "8px", padding: "0.35rem 0.75rem", cursor: "pointer",
              color: viewMode === "rolodex" ? "var(--jop-gold)" : "rgba(255,218,173,0.5)",
              fontSize: "0.78rem", fontWeight: 600, fontFamily: "var(--jop-display)",
            }}>◇ Rolodex</button>
            <button onClick={() => setViewMode("grid")} style={{
              background: viewMode === "grid" ? "rgba(252,179,31,0.15)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${viewMode === "grid" ? "rgba(252,179,31,0.4)" : "rgba(255,255,255,0.12)"}`,
              borderRadius: "8px", padding: "0.35rem 0.75rem", cursor: "pointer",
              color: viewMode === "grid" ? "var(--jop-gold)" : "rgba(255,218,173,0.5)",
              fontSize: "0.78rem", fontWeight: 600, fontFamily: "var(--jop-display)",
            }}>⊞ Grid</button>
          </div>

          {/* Showpieces toggle — only when Dan has curated at least one. Default
              view is Showpieces; "All pancakes" reveals the full Pancakeopedia. */}
          {hasShowcases && (
            <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "center", gap: "0.4rem", flexWrap: "wrap" }}>
              {[{ on: true, label: "★ Showpieces" }, { on: false, label: "All pancakes" }].map(opt => {
                const active = showcaseOnly === opt.on;
                return (
                  <button key={String(opt.on)} onClick={() => { setShowcaseOnly(opt.on); setIdx(0); }} style={{
                    background: active ? "rgba(252,179,31,0.18)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${active ? "rgba(252,179,31,0.55)" : "rgba(255,255,255,0.12)"}`,
                    borderRadius: "999px", padding: "0.3rem 0.85rem", cursor: "pointer",
                    color: active ? "var(--jop-gold)" : "rgba(255,218,173,0.5)",
                    fontSize: "0.8rem", fontWeight: active ? 700 : 500, fontFamily: "var(--jop-display)",
                  }}>{opt.label}</button>
                );
              })}
            </div>
          )}

          {/* Sort + type filter */}
          <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "center", gap: "0.4rem", flexWrap: "wrap" }}>
            <select
              value={sortBy}
              onChange={e => { setSortBy(e.target.value); setIdx(0); }}
              style={{
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "8px", padding: "0.35rem 0.6rem", cursor: "pointer",
                color: "rgba(255,218,173,0.7)", fontSize: "0.78rem", fontFamily: "var(--jop-display)",
                outline: "none",
              }}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="fastest-draw">Fastest draw</option>
              <option value="fastest-total">Fastest total</option>
            </select>
            {["flash", "custom", "freestyle_flash"].map(type => {
              const label = { flash: "Flash", custom: "Custom", freestyle_flash: "Freestyle" }[type];
              const active = filterType === type;
              return (
                <button key={type} onClick={() => { setFilterType(active ? null : type); setIdx(0); }} style={{
                  background: active ? "rgba(252,179,31,0.15)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${active ? "rgba(252,179,31,0.4)" : "rgba(255,255,255,0.12)"}`,
                  borderRadius: "8px", padding: "0.35rem 0.65rem", cursor: "pointer",
                  color: active ? "var(--jop-gold)" : "rgba(255,218,173,0.5)",
                  fontSize: "0.78rem", fontWeight: 600, fontFamily: "var(--jop-display)",
                }}>{label}</button>
              );
            })}
          </div>

          {/* Tag filter pills — most common first */}
          {allTags.length > 0 && (
            <div style={{
              marginTop: "0.6rem", display: "flex", gap: "0.35rem",
              justifyContent: "center", flexWrap: "wrap",
              maxWidth: "600px", margin: "0.6rem auto 0",
            }}>
              {allTags.slice(0, 12).map(tag => {
                const active = filterTag === tag;
                return (
                  <button key={tag} onClick={() => { setFilterTag(active ? null : tag); setIdx(0); }} style={{
                    background: active ? "rgba(74,164,225,0.2)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? "rgba(74,164,225,0.5)" : "rgba(255,255,255,0.1)"}`,
                    borderRadius: "999px", padding: "0.22rem 0.6rem", cursor: "pointer",
                    color: active ? "var(--jop-highlight-blue)" : "rgba(255,218,173,0.45)",
                    fontSize: "0.72rem", fontWeight: active ? 700 : 500,
                    fontFamily: "var(--jop-display)", whiteSpace: "nowrap",
                  }}>{tag}</button>
                );
              })}
            </div>
          )}

          {/* Active filter indicator */}
          {(filterTag || filterType) && (
            <div style={{ marginTop: "0.5rem", display: "flex", justifyContent: "center" }}>
              <button onClick={() => { setFilterTag(null); setFilterType(null); setIdx(0); }} style={{
                background: "rgba(239,83,126,0.1)", border: "1px solid rgba(239,83,126,0.3)",
                borderRadius: "999px", padding: "0.25rem 0.8rem", cursor: "pointer",
                color: "var(--jop-pink)", fontSize: "0.72rem", fontWeight: 600,
                fontFamily: "var(--jop-display)",
              }}>Clear filters</button>
            </div>
          )}
        </header>

        {viewMode === "grid" && (<>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: "0.5rem",
            padding: "0 1.25rem 2rem",
            maxWidth: "900px",
            margin: "0 auto",
          }}>
            {displayed.slice(0, visibleCount).map((pancake, i) => (
              <button
                key={pancake.id}
                onClick={() => { setGridFocus(i); setIdx(i); }}
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "12px",
                  padding: "8px",
                  cursor: "pointer",
                  aspectRatio: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.3rem",
                  transition: "border-color 0.2s ease, background 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(252,179,31,0.4)"; e.currentTarget.style.background = "rgba(252,179,31,0.06)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
              >
                <img
                  src={pancake.thumbUrl || pancake.cutoutUrl}
                  alt={pancake.title}
                  loading="lazy"
                  style={{
                    width: "100%", height: "100%",
                    objectFit: "contain",
                    filter: [
                      pancake.hasMeditation ? "drop-shadow(0 0 6px rgba(252,179,31,0.3))" : "",
                      pancake.testimonial ? "drop-shadow(0 0 6px rgba(239,83,126,0.3))" : "",
                    ].filter(Boolean).join(" ") || "none",
                  }}
                />
                <div style={{
                  color: "rgba(255,218,173,0.6)", fontSize: "0.6rem",
                  fontFamily: "monospace", letterSpacing: "0.05em",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  width: "100%", textAlign: "center",
                }}>
                  {pancake.serial}
                </div>
              </button>
            ))}
          </div>

          {/* Load more button */}
          {visibleCount < displayed.length && (
            <div style={{ textAlign: "center", padding: "0 1.25rem 2rem" }}>
              <button
                onClick={() => setVisibleCount(c => Math.min(c + 40, displayed.length))}
                style={{
                  background: "rgba(252,179,31,0.12)", border: "1px solid rgba(252,179,31,0.3)",
                  borderRadius: "999px", padding: "0.65rem 1.8rem", cursor: "pointer",
                  color: "var(--jop-gold)", fontSize: "0.88rem", fontWeight: 700,
                  fontFamily: "var(--jop-display)",
                }}
              >
                Load more ({displayed.length - visibleCount} remaining)
              </button>
            </div>
          )}

          {/* Grid focus overlay — selected pancake lifts out of the grid */}
          {gridFocus !== null && displayed[gridFocus] && (() => {
            const gp = displayed[gridFocus];
            const gm = meditations[gp.id];
            return (
              <div
                onClick={() => setGridFocus(null)}
                style={{
                  position: "fixed", inset: 0, zIndex: 100,
                  background: "rgba(8,26,42,0.88)",
                  backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                  display: "flex", flexDirection: "column", alignItems: "center",
                  overflowY: "auto",
                  animation: "jopPopFade 0.2s ease",
                  padding: "5rem 1.25rem 3rem",
                }}
              >
                <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "480px", width: "100%", textAlign: "center" }}>
                  {/* Close button */}
                  <button onClick={() => setGridFocus(null)} style={{
                    position: "fixed", top: "1rem", right: "1rem",
                    background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "50%", width: "40px", height: "40px",
                    color: "rgba(255,218,173,0.7)", fontSize: "1.2rem", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 101,
                  }}>✕</button>

                  {/* Hero pancake */}
                  <div style={{
                    width: Math.min(320, window.innerWidth * 0.7), height: Math.min(320, window.innerWidth * 0.7),
                    margin: "0 auto 1rem", display: "flex", alignItems: "center", justifyContent: "center",
                    animation: "jopPopIn 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
                  }}>
                    <img
                      src={gp.cutoutUrl || gp.thumbUrl}
                      alt={gp.title}
                      style={{
                        maxWidth: "100%", maxHeight: "100%", objectFit: "contain",
                        filter: `drop-shadow(0 10px 24px rgba(0,0,0,0.5))${gp.hasMeditation ? " drop-shadow(0 0 18px rgba(252,179,31,0.35))" : ""}${gp.testimonial ? " drop-shadow(0 0 14px rgba(239,83,126,0.35))" : ""}`,
                      }}
                    />
                  </div>

                  {/* Title + serial */}
                  <div style={{
                    fontSize: "clamp(1.3rem, 3.5vw, 1.8rem)",
                    fontFamily: "var(--jop-script)",
                    color: "var(--jop-gold)", fontWeight: 700, marginBottom: "0.3rem",
                  }}>{gp.title}</div>
                  <div style={{ color: "rgba(255,218,173,0.4)", fontSize: "0.72rem", fontFamily: "monospace", marginBottom: "0.75rem", letterSpacing: "0.08em" }}>
                    {gp.serial}
                  </div>

                  {/* Stats */}
                  <div style={{ display: "flex", gap: "1.25rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
                    <Stat label="Type" value={TYPE_LABELS[gp.orderType] || gp.orderType} />
                    {gp.eventName && <Stat label="Event" value={gp.eventName} />}
                    {gp.eventDateMs && <Stat label="Date" value={fmtDate(gp.eventDateMs)} />}
                    {gp.drawTimeMs && <Stat label="Draw" value={fmtMs(gp.drawTimeMs)} />}
                    {gp.cookTimeMs && <Stat label="Cook" value={fmtMs(gp.cookTimeMs)} />}
                    {gp.artistName && <Stat label="Artist" value={gp.artistName} />}
                  </div>

                  {/* Tags */}
                  {gp.tags?.length > 0 && (
                    <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
                      {gp.tags.map(tag => (
                        <span key={tag} style={{
                          background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.25)",
                          borderRadius: "999px", padding: "0.2rem 0.65rem",
                          fontSize: "0.72rem", color: "var(--jop-highlight-blue)", fontWeight: 600,
                        }}>{tag}</span>
                      ))}
                    </div>
                  )}

                  {/* Meditation */}
                  {gm?.meditation && (
                    <div style={{
                      background: "rgba(252,179,31,0.06)", border: "1px solid rgba(252,179,31,0.2)",
                      borderRadius: "14px", padding: "1.25rem 1.5rem", textAlign: "left", marginBottom: "1rem",
                    }}>
                      <div style={{ color: "var(--jop-gold)", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.5rem" }}>Meditation</div>
                      <p style={{ color: "var(--jop-cream)", fontSize: "0.9rem", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{gm.meditation}</p>
                    </div>
                  )}

                  {/* Testimonial */}
                  {gp.testimonial && (
                    <div style={{
                      background: "rgba(239,83,126,0.06)", border: "1px solid rgba(239,83,126,0.2)",
                      borderRadius: "14px", padding: "1.25rem 1.5rem", textAlign: "left", marginBottom: "1rem",
                    }}>
                      <div style={{ color: "var(--jop-pink)", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.5rem" }}>Guest Review</div>
                      <p style={{ color: "var(--jop-cream)", fontSize: "0.9rem", lineHeight: 1.65, margin: 0, fontStyle: "italic" }}>"{gp.testimonial.text}"</p>
                      {gp.testimonial.authorName && (
                        <div style={{ color: "rgba(255,218,173,0.6)", fontSize: "0.78rem", marginTop: "0.5rem", fontWeight: 600 }}>— {gp.testimonial.authorName}</div>
                      )}
                    </div>
                  )}

                  {/* AI description (alt text) — bottom slot so guest voice + reflection lead */}
                  {gp.aiDescription && (
                    <p style={{
                      color: "rgba(255,218,173,0.55)", fontSize: "0.85rem",
                      fontStyle: "italic", lineHeight: 1.55, margin: "0 0 1rem",
                    }}>
                      "{gp.aiDescription}" <span style={{ color: "var(--jop-gold)", fontStyle: "normal" }}>— Jacques</span>
                    </p>
                  )}

                  {/* View in rolodex */}
                  <button onClick={() => { setViewMode("rolodex"); setGridFocus(null); }} style={{
                    background: "rgba(252,179,31,0.12)", border: "1px solid rgba(252,179,31,0.3)",
                    borderRadius: "999px", padding: "0.55rem 1.2rem", cursor: "pointer",
                    color: "var(--jop-gold)", fontSize: "0.82rem", fontWeight: 600,
                    fontFamily: "var(--jop-display)",
                  }}>
                    ◇ View in Rolodex
                  </button>
                </div>
              </div>
            );
          })()}
        </>)}

        {/* Coverflow rolodex + detail panel */}
        {viewMode === "rolodex" && (<>
        <div
          ref={containerRef}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={() => { if (dragRef.current) { dragRef.current = null; setDragOffset(0); } }}
          style={{
            position: "relative",
            height: cardSize * 1.35 + 40,
            perspective: "1200px",
            cursor: dragRef.current ? "grabbing" : "grab",
            userSelect: "none",
            touchAction: "pan-y",
            overscrollBehaviorX: "none",
          }}
        >
          {displayed.map((pancake, i) => {
            const dist = i - safeIdx + (dragOffset / (cardSize * CARD_GAP));
            if (Math.abs(dist) > VISIBLE_NEIGHBORS + 0.5) return null;

            const absD = Math.abs(dist);
            const isFocused = absD < 0.3;
            // Snap focused card to dead center — no drift from fractional dist
            const tx = isFocused && !dragRef.current ? 0 : dist * cardSize * CARD_GAP;
            const baseScale = Math.max(0.45, (isFocused ? 1.3 : 1) - absD * 0.18);
            const scale = isFocused ? baseScale * pinchScale : baseScale;
            const opacity = Math.max(0.15, 1 - absD * 0.3);
            const notDragging = !dragRef.current;
            const rotY = isFocused && notDragging ? 0 : Math.max(-45, Math.min(45, dist * -15));
            const z = isFocused && notDragging ? 0 : -Math.abs(dist) * 60;

            return (
              <div
                key={pancake.id}
                onClick={() => {
                  if (dragRef.current?.moved) return;
                  if (isFocused && window.innerWidth >= 768) { setLightbox(lb => !lb); }
                  else if (!isFocused) { setLightbox(false); setIdx(i); }
                }}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: cardSize,
                  height: cardSize,
                  transform: `translate3d(calc(-50% + ${tx + (isFocused ? pinchTx : 0)}px), calc(-50% + ${isFocused ? pinchTy : 0}px), ${z}px) rotateY(${rotY}deg) scale(${scale})`,
                  opacity,
                  transition: (dragRef.current || pinchRef.current) ? "none" : "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease",
                  cursor: isFocused ? (window.innerWidth >= 768 ? "zoom-in" : "default") : "pointer",
                  zIndex: 100 - Math.round(absD * 10),
                  transformStyle: "preserve-3d",
                  willChange: "transform, opacity",
                }}
              >
                <img
                  src={pancake.thumbUrl || pancake.cutoutUrl}
                  alt={pancake.title}
                  loading="eager"
                  draggable={false}
                  style={{
                    maxWidth: "100%", maxHeight: "100%",
                    objectFit: "contain",
                    filter: (() => {
                      // Gradual brightness: 1.0 at center, dims smoothly with distance
                      const bright = Math.max(0.35, 1 - absD * 0.2);
                      const shadow = "drop-shadow(0 8px 16px rgba(0,0,0,0.5))";
                      const medGlow = pancake.hasMeditation ? ` drop-shadow(0 0 ${isFocused ? 16 : 8}px rgba(252,179,31,0.4))` : "";
                      const testGlow = pancake.testimonial ? ` drop-shadow(0 0 ${isFocused ? 14 : 7}px rgba(239,83,126,0.4))` : "";
                      return isFocused
                        ? `${shadow}${medGlow}${testGlow}`
                        : `brightness(${bright.toFixed(2)}) ${shadow}${medGlow}${testGlow}`;
                    })(),
                    transition: "filter 0.4s ease",
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Detail panel — slides up for focused pancake */}
        <div style={{
          maxWidth: "560px", margin: "0 auto",
          padding: "1.5rem 1.5rem 2rem",
          textAlign: "center",
        }}>
          <div style={{
            fontSize: "clamp(1.3rem, 3.5vw, 1.8rem)",
            fontFamily: "var(--jop-script)",
            color: "var(--jop-gold)",
            fontWeight: 700,
            marginBottom: "0.3rem",
          }}>
            {focused.title}
          </div>

          <div style={{ color: "rgba(255,218,173,0.4)", fontSize: "0.72rem", fontFamily: "monospace", marginBottom: "0.75rem", letterSpacing: "0.08em" }}>
            {focused.serial}
          </div>

          {/* Stats row */}
          <div style={{ display: "flex", gap: "1.25rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
            <Stat label="Type" value={TYPE_LABELS[focused.orderType] || focused.orderType} />
            {focused.eventName && <Stat label="Event" value={focused.eventName} />}
            {focused.eventDateMs && <Stat label="Date" value={fmtDate(focused.eventDateMs)} />}
            {focused.drawTimeMs && <Stat label="Draw" value={fmtMs(focused.drawTimeMs)} />}
            {focused.cookTimeMs && <Stat label="Cook" value={fmtMs(focused.cookTimeMs)} />}
            {focused.artistName && <Stat label="Artist" value={focused.artistName} />}
          </div>

          {/* Tags */}
          {focused.tags?.length > 0 && (
            <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
              {focused.tags.map(tag => (
                <span key={tag} style={{
                  background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.25)",
                  borderRadius: "999px", padding: "0.2rem 0.65rem",
                  fontSize: "0.72rem", color: "var(--jop-highlight-blue)", fontWeight: 600,
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Meditation */}
          {meditation?.meditation && (
            <div style={{
              background: "rgba(252,179,31,0.06)",
              border: "1px solid rgba(252,179,31,0.2)",
              borderRadius: "14px",
              padding: "1.25rem 1.5rem",
              textAlign: "left",
              marginBottom: "1rem",
            }}>
              <div style={{ color: "var(--jop-gold)", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.5rem" }}>
                Meditation
              </div>
              <p style={{ color: "var(--jop-cream)", fontSize: "0.9rem", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>
                {meditation.meditation}
              </p>
            </div>
          )}

          {/* Testimonial */}
          {focused.testimonial && (
            <div style={{
              background: "rgba(239,83,126,0.06)", border: "1px solid rgba(239,83,126,0.2)",
              borderRadius: "14px", padding: "1.25rem 1.5rem", textAlign: "left", marginBottom: "1rem",
            }}>
              <div style={{ color: "var(--jop-pink)", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.5rem" }}>
                Guest Review
              </div>
              <p style={{ color: "var(--jop-cream)", fontSize: "0.9rem", lineHeight: 1.65, margin: 0, fontStyle: "italic" }}>
                "{focused.testimonial.text}"
              </p>
              {focused.testimonial.authorName && (
                <div style={{ color: "rgba(255,218,173,0.6)", fontSize: "0.78rem", marginTop: "0.5rem", fontWeight: 600 }}>
                  — {focused.testimonial.authorName}
                </div>
              )}
            </div>
          )}

          {/* AI description (alt text) — bottom slot so guest voice + reflection lead */}
          {focused.aiDescription && (
            <p style={{
              color: "rgba(255,218,173,0.55)", fontSize: "0.85rem",
              fontStyle: "italic", lineHeight: 1.55, margin: "0 0 1rem",
            }}>
              "{focused.aiDescription}" <span style={{ color: "var(--jop-gold)", fontStyle: "normal" }}>— Jacques</span>
            </p>
          )}

          {/* Link to full meditation page if exists */}
          {focused.hasMeditation && (
            <a href={`/m/${focused.id}`} style={{
              color: "var(--jop-pink)", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none",
            }}>
              Read the full meditation →
            </a>
          )}
        </div>
        </>)}
      </main>

      <SiteFooter />

      {/* Lightbox — full-screen image on click (desktop) */}
      {lightbox && focused && (
        <div
          onClick={() => setLightbox(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.92)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
            animation: "jopPopFade 0.25s ease",
          }}
        >
          <img
            src={focused.cutoutUrl || focused.thumbUrl}
            alt={focused.title}
            style={{
              maxWidth: "90vw", maxHeight: "85vh",
              objectFit: "contain",
              filter: focused.hasMeditation ? "drop-shadow(0 0 24px rgba(252,179,31,0.35))" : "drop-shadow(0 12px 30px rgba(0,0,0,0.6))",
              pointerEvents: "none",
            }}
          />
          <button onClick={() => setLightbox(false)} style={{
            position: "absolute", top: "1.5rem", right: "1.5rem",
            background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "50%", width: "40px", height: "40px",
            color: "rgba(255,218,173,0.7)", fontSize: "1.2rem", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>✕</button>
          <div style={{
            position: "absolute", bottom: "2rem", left: "50%", transform: "translateX(-50%)",
            color: "rgba(255,218,173,0.6)", fontSize: "0.85rem", fontFamily: "var(--jop-display)",
            textAlign: "center",
          }}>
            <div style={{ fontWeight: 700, color: "var(--jop-gold)", fontSize: "1.1rem", marginBottom: "0.25rem" }}>{focused.title}</div>
            <div style={{ fontFamily: "monospace", fontSize: "0.7rem", letterSpacing: "0.08em" }}>{focused.serial}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ color: "rgba(255,218,173,0.4)", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.15rem" }}>{label}</div>
      <div style={{ color: "var(--jop-cream)", fontSize: "0.82rem", fontWeight: 600 }}>{value}</div>
    </div>
  );
}
